import { add, set } from "date-fns";
import React, { useEffect, useState } from "react";
import DateSelector from "./DateSelector/DateSelector";
import MonthYearControls from "./MonthYearControls/MonthYearControls";
import styled from "styled-components";
import { today } from "../../../utils";
import { useKeyboardNavigation } from "./useKeyboardNavigation/useKeyboardNavigation";
export type CalendarDatePickerProps = {
  /**
   * Should the user be allowed to select dates in the past.
   * Unless this is explicitly set to true, buttons for past dates
   * will be disabled
   */
  allowPastSelection?: boolean;
  /**
   * Optional array of available dates
   * If at least one or more available dates are provided,
   * the calendar will only allow the selection of those dates.
   */
  availableDates?: Date[];
  /**
   * Offset number of months from the selected date that is displayed
   * in the month selector.
   */
  monthOffset: number;
  /**
   * Function called when the month changes
   */
  onMonthChange: (newOffset: number) => void;
  /**
   * Function that is called when a date is selected in the calendar.
   */
  onSelectDate: (selectedDate: Date) => void;
  /**
   * Currently selected date in the calendar.
   */
  selectedDate?: Date;
};
export function CalendarDatePicker({
  allowPastSelection,
  availableDates,
  monthOffset,
  onMonthChange,
  onSelectDate: parentOnSelectDate,
  selectedDate
}: CalendarDatePickerProps): React.ReactElement {
  const [monthYearControlsOpen, setMonthYearControlsOpen] = useState<boolean>(false);
  const updateMonthOffset = (newOffset: number) => {
    const firstOfNewMonth = set(add(today, {
      months: newOffset
    }), {
      date: 1
    });
    onMonthChange(newOffset);
    setFocusedDate(firstOfNewMonth);
  };
  const {
    focusedDate,
    setFocusedDate,
    handleKeyboardNavigation
  } = useKeyboardNavigation({
    monthOffset,
    onSelectDate: parentOnSelectDate,
    selectedDate,
    updateMonthOffset
  });
  const onSelectDate = (newDate: Date) => {
    setMonthYearControlsOpen(false);
    parentOnSelectDate(newDate);
  };
  useEffect(() => {
    if (selectedDate && selectedDate !== focusedDate) {
      setFocusedDate(selectedDate);
    }
  }, [focusedDate, selectedDate, setFocusedDate]);
  return <Container data-sentry-element="Container" data-sentry-component="CalendarDatePicker" data-sentry-source-file="CalendarDatePicker.tsx">
      <MonthYearControls {...{
      allowPastSelection,
      monthOffset,
      onChange: updateMonthOffset,
      open: monthYearControlsOpen,
      selectedDate,
      toggleOpen: () => setMonthYearControlsOpen(!monthYearControlsOpen)
    }} data-sentry-element="MonthYearControls" data-sentry-source-file="CalendarDatePicker.tsx" />
      <DateSelector {...{
      allowPastSelection,
      availableDates,
      focusedDate,
      handleKeyboardNavigation,
      monthOffset,
      onSelectDate,
      selectedDate,
      setFocusedDate
    }} data-sentry-element="DateSelector" data-sentry-source-file="CalendarDatePicker.tsx" />
    </Container>;
}
const Container = styled.div`
  width: min(100%, 22.5rem);
`;
export default CalendarDatePicker;